import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useCompContext } from '@/context/__project__/CompContext'

import { useYouTubeAPI } from '../hooks/useYouTubeAPI'
import { VideoBackgroundProps } from '../types'

/**
 * YoutubeBackground - Renders a YouTube video as background with advanced playback controls
 */
export const YoutubeBackground = React.memo(
  ({ videoSrc, layerStyle, index, values, device, containerRef }: VideoBackgroundProps) => {
    const { component } = useCompContext()

    const [videoError, setVideoError] = useState(false)
    const [containerSize, setContainerSize] = useState({ width: window.innerWidth, height: window.innerHeight })
    const [scale, setScale] = useState(device === 'phone' ? 2.2 : 1.5)

    const playerContainerRef = useRef<HTMLDivElement>(null)
    const playerRef = useRef<any>(null)
    const [isReady, setIsReady] = useState(false)
    const autoplayWatchRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const stallWatchRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const didMutedRetryRef = useRef(false)
    const loopCheckRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const lastPlayTimeRef = useRef<number>(0)
    const consecutiveEndedCountRef = useRef<number>(0)
    const loopAttemptsRef = useRef<number>(0)
    const maxLoopAttempts = 10

    const clearWatchdogs = () => {
      if (autoplayWatchRef.current) {
        clearTimeout(autoplayWatchRef.current)
        autoplayWatchRef.current = null
      }
      if (stallWatchRef.current) {
        clearTimeout(stallWatchRef.current)
        stallWatchRef.current = null
      }
      if (loopCheckRef.current) {
        clearInterval(loopCheckRef.current)
        loopCheckRef.current = null
      }
    }

    useEffect(() => {
      if (!isReady) {
        return
      }

      const updateContainerSize = () => {
        if (!containerRef?.current) {
          setContainerSize({ width: window.innerWidth, height: window.innerHeight })
          return
        }

        const container = containerRef.current
        const { width, height } = container.getBoundingClientRect()

        setContainerSize({ width, height })

        const videoRatio = 16 / 9
        const containerRatio = width / height

        let newScale
        if (containerRatio < videoRatio) {
          newScale = containerRatio / videoRatio
        } else {
          newScale = videoRatio / containerRatio
        }

        const coverageScale = 1.2
        const phoneScale = containerRatio > 0.5 ? 1.5 : containerRatio > 0.3 ? 1.8 : 2

        const deviceScale = device === 'phone' ? phoneScale : 1.1

        setScale(Math.max(newScale, 1) * coverageScale * deviceScale)
      }

      updateContainerSize()

      const resizeObserver = new ResizeObserver(updateContainerSize)

      if (containerRef?.current) {
        resizeObserver.observe(containerRef.current)
      }

      window.addEventListener('resize', updateContainerSize)

      return () => {
        resizeObserver.disconnect()
        window.removeEventListener('resize', updateContainerSize)
      }
    }, [containerRef, device, isReady])

    const videoId = useMemo(() => {
      return (
        videoSrc.match(
          /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([^"&?\/\s]{11})/
        )?.[1] || ''
      )
    }, [videoSrc])

    const apiReady = useYouTubeAPI()

    useEffect(() => {
      if (!apiReady || !videoId) return

      const createPlayer = () => {
        if (!playerContainerRef.current) return

        if (playerRef.current) {
          playerRef.current.destroy()
        }

        // Reset refs saat membuat player baru
        lastPlayTimeRef.current = 0
        consecutiveEndedCountRef.current = 0
        didMutedRetryRef.current = false
        loopAttemptsRef.current = 0

        playerRef.current = new window.YT.Player(playerContainerRef.current, {
          videoId: videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            origin: window.location.origin,
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            enablejsapi: 1,
            fs: 0,
            loop: values.loop ? 1 : 0,
            mute: values.muted ? 1 : 0,
            playsinline: 1,
            rel: 0,
            showinfo: 0,
            modestbranding: 1,
            iv_load_policy: 3,
            playlist: values.loop ? videoId : undefined,
          },
          events: {
            onReady: (event: any) => {
              try {
                event.target.playVideo()
                lastPlayTimeRef.current = Date.now()
              } catch (e) {
                /* no-op: autoplay may be blocked */
              }
              setIsReady(true)

              if (values.loop) {
                clearWatchdogs()
                loopCheckRef.current = setInterval(() => {
                  if (!playerRef.current) return

                  try {
                    const state = playerRef.current.getPlayerState()

                    if (state === window.YT.PlayerState.ENDED) {
                      loopAttemptsRef.current++

                      if (loopAttemptsRef.current >= maxLoopAttempts) {
                        console.warn('YouTube loop limit reached, restarting player...')
                        loopAttemptsRef.current = 0
                        if (playerRef.current) {
                          playerRef.current.destroy()
                          setTimeout(() => createPlayer(), 1000)
                        }
                        return
                      }

                      consecutiveEndedCountRef.current++
                      if (consecutiveEndedCountRef.current > 2) {
                        console.warn('YouTube loop stuck, restarting player...')
                        consecutiveEndedCountRef.current = 0
                        loopAttemptsRef.current = 0
                        if (playerRef.current) {
                          playerRef.current.destroy()
                          setTimeout(() => createPlayer(), 1000)
                        }
                        return
                      }

                      try {
                        playerRef.current.seekTo(0, true)
                        setTimeout(() => {
                          if (playerRef.current) {
                            playerRef.current.playVideo()
                            consecutiveEndedCountRef.current = 0
                            lastPlayTimeRef.current = Date.now()
                          }
                        }, 100)
                      } catch (e) {
                        console.error('Error restarting YouTube video:', e)
                        if (playerRef.current) {
                          playerRef.current.destroy()
                          setTimeout(() => createPlayer(), 1000)
                        }
                      }
                    } else if (state === window.YT.PlayerState.PLAYING) {
                      consecutiveEndedCountRef.current = 0
                      lastPlayTimeRef.current = Date.now()
                    }
                  } catch {
                    console.error('Error monitoring YouTube video loop')
                  }
                }, 3000)
              }

              if (autoplayWatchRef.current) clearTimeout(autoplayWatchRef.current)
              autoplayWatchRef.current = setTimeout(() => {
                const player = playerRef.current
                const state = player?.getPlayerState?.()
                const currentTime = player?.getCurrentTime?.()
                if (state !== window.YT.PlayerState.PLAYING || (typeof currentTime === 'number' && currentTime <= 0)) {
                  if (!didMutedRetryRef.current) {
                    try {
                      player?.mute?.()
                      player?.playVideo?.()
                    } catch {
                      console.error('Error muting and playing YouTube video')
                    }
                    didMutedRetryRef.current = true
                    if (autoplayWatchRef.current) clearTimeout(autoplayWatchRef.current)
                    autoplayWatchRef.current = setTimeout(() => {
                      const retryState = player?.getPlayerState?.()
                      const retryTime = player?.getCurrentTime?.()
                      if (
                        retryState !== window.YT.PlayerState.PLAYING ||
                        (typeof retryTime === 'number' && retryTime <= 0)
                      ) {
                        setVideoError(true)
                      }
                    }, 2500)
                  } else {
                    setVideoError(true)
                  }
                }
              }, 3500)
            },
            onError: () => {
              setVideoError(true)
            },
            onStateChange: (event: any) => {
              if (event.data === window.YT.PlayerState.ENDED) {
                if (values.loop) {
                  loopAttemptsRef.current++

                  if (loopAttemptsRef.current >= maxLoopAttempts) {
                    console.warn('YouTube loop limit reached in onStateChange, restarting player...')
                    loopAttemptsRef.current = 0
                    consecutiveEndedCountRef.current = 0
                    if (playerRef.current) {
                      playerRef.current.destroy()
                      setTimeout(() => createPlayer(), 1000)
                    }
                    return
                  }

                  consecutiveEndedCountRef.current++
                  setTimeout(() => {
                    try {
                      const player = event.target || playerRef.current
                      if (player && player.getPlayerState() === window.YT.PlayerState.ENDED) {
                        player.seekTo(0, true)
                        setTimeout(() => {
                          if (player) {
                            player.playVideo()
                            consecutiveEndedCountRef.current = 0
                            lastPlayTimeRef.current = Date.now()
                          }
                        }, 100)
                      }
                    } catch (e) {
                      console.error('Error in onStateChange loop:', e)
                      if (playerRef.current) {
                        playerRef.current.destroy()
                        setTimeout(() => createPlayer(), 1000)
                      }
                    }
                  }, 200)
                }
                return
              }

              if (event.data === window.YT.PlayerState.PLAYING) {
                clearWatchdogs()
                consecutiveEndedCountRef.current = 0
                lastPlayTimeRef.current = Date.now()
                return
              }

              if (
                event.data === window.YT.PlayerState.UNSTARTED ||
                event.data === window.YT.PlayerState.CUED ||
                event.data === window.YT.PlayerState.PAUSED
              ) {
                if (stallWatchRef.current) clearTimeout(stallWatchRef.current)
                stallWatchRef.current = setTimeout(() => {
                  const player = playerRef.current
                  const state = player?.getPlayerState?.()
                  if (state !== window.YT.PlayerState.PLAYING) {
                    // if not already retried, try muted retry
                    if (!didMutedRetryRef.current) {
                      try {
                        player?.mute?.()
                        player?.playVideo?.()
                      } catch (e) {
                        /* no-op */
                      }
                      didMutedRetryRef.current = true
                      if (stallWatchRef.current) clearTimeout(stallWatchRef.current)
                      stallWatchRef.current = setTimeout(() => {
                        const retryState = player?.getPlayerState?.()
                        if (retryState !== window.YT.PlayerState.PLAYING) {
                          setVideoError(true)
                        }
                      }, 2000)
                    } else {
                      setVideoError(true)
                    }
                  }
                }, 4000)
              }
            },
          },
        })
      }

      createPlayer()

      return () => {
        clearWatchdogs()
        if (playerRef.current) {
          playerRef.current.destroy()
        }
        didMutedRetryRef.current = false
        consecutiveEndedCountRef.current = 0
        lastPlayTimeRef.current = 0
        loopAttemptsRef.current = 0
      }
    }, [apiReady, videoId, values.loop, values.muted])

    if (videoError) {
      return null
    }

    return (
      <div
        style={{
          ...layerStyle,
          zIndex: -index - 1,
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <div
          ref={playerContainerRef}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: `${containerSize.width * (16 / 9)}px`,
            height: `${containerSize.height}px`,
            transform: `translate(-50%, -50%) scale(${scale})`,
            pointerEvents: 'none',
            overflow: 'hidden',
            backgroundColor: 'black',
          }}
        />
      </div>
    )
  }
)
YoutubeBackground.displayName = 'YoutubeBackground'
